/**
 * D16-B — canonical Web supervision media-leg construction (single Manager↔Operator auxiliary leg).
 *
 * PURE + dependency-injected: peer connections and tracks come in as parameters, so the exact
 * customer-exclusion logic is unit-testable WITHOUT a live browser (runtime audio proof is a Milestone-6
 * gate). The Customer PeerConnection is NEVER accepted by any construction/track function here, so the
 * supervisor microphone can never be added to the Customer — structural exclusion by construction.
 *
 * The backend emits a SINGLE server-authorized leg with target_role === 'operator' (see
 * operator_analytics_routes.py _supervisor_legs); this module refuses to build anything else. The primary
 * Customer↔Operator PeerConnection is never referenced here and is never renegotiated.
 */
export const SUPERVISOR_MODES = { JOIN: 'join', WHISPER: 'whisper' };

/**
 * Build the auxiliary Supervisor↔Operator peer connection from a server-authorized leg descriptor.
 * Only an operator-targeted leg is honored; a customer-targeted leg is refused (defense-in-depth).
 * @param {{target_role:string, conn_id:string, target_user_id:string, manager_direction:string}} leg
 * @param {{createPeerConnection:Function, iceServers?:Array}} deps
 */
export function createOperatorSupervisionLeg(leg, { createPeerConnection, iceServers } = {}) {
  if (!leg || leg.target_role !== 'operator') {
    throw new Error('supervision: refusing to build a leg that does not target the operator');
  }
  if (typeof createPeerConnection !== 'function') {
    throw new Error('supervision: createPeerConnection dependency is required');
  }
  const pc = createPeerConnection({ iceServers: iceServers || [] });
  // P0-D M-2/M-3 — the Manager must HEAR THE CALL in BOTH modes. The operator forwards TWO separate
  // tracks (customer-remote + operator-local), so the Manager offer MUST present two receive slots.
  // Without them: JOIN (recvonly, no addTrack) created an offer with NO audio m-line → no media
  // negotiated at all; WHISPER received at most one side. These two recvonly transceivers give the
  // Manager a receive slot for each forwarded track for BOTH modes; whisper's startWhisper() then adds
  // the mic (reusing one slot → sendrecv, so the Operator hears the Manager). The Customer still has no
  // supervisor leg, so whisper privacy remains structural. Additive: never touches the primary call.
  try {
    if (typeof pc.addTransceiver === 'function') {
      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });
    }
  } catch (_) { /* engine/stub without addTransceiver — best-effort (unit tests use a minimal PC double) */ }
  return {
    pc,
    connId: leg.conn_id,
    targetRole: 'operator',
    targetUserId: leg.target_user_id,
    managerDirection: leg.manager_direction, // 'sendrecv' (whisper) | 'recvonly' (join listen-only)
    mode: leg.manager_direction === 'sendrecv' ? SUPERVISOR_MODES.WHISPER : SUPERVISOR_MODES.JOIN,
    whisperSender: null,
  };
}

/**
 * Start whisper: attach the manager microphone track ONLY to the operator auxiliary leg.
 * Never touches the Customer — the Customer PC is not even a parameter.
 * @returns the RTCRtpSender for the mic (so it can be removed on stop).
 */
export function startWhisper(auxLeg, micTrack, micStream) {
  if (!auxLeg || auxLeg.targetRole !== 'operator') {
    throw new Error('supervision: whisper requires the operator auxiliary leg');
  }
  if (!micTrack) throw new Error('supervision: a microphone track is required for whisper');
  const sender = auxLeg.pc.addTrack(micTrack, micStream);
  auxLeg.mode = SUPERVISOR_MODES.WHISPER;
  auxLeg.whisperSender = sender;
  return sender;
}

/**
 * Stop whisper: remove/stop ONLY the manager send track. The auxiliary leg stays connected (the manager
 * remains joined / listen-only); the primary call is untouched.
 */
export function stopWhisper(auxLeg) {
  if (auxLeg && auxLeg.whisperSender) {
    try { auxLeg.pc.removeTrack(auxLeg.whisperSender); } catch (_) { /* already removed */ }
    const tr = auxLeg.whisperSender.track;
    if (tr && typeof tr.stop === 'function') { try { tr.stop(); } catch (_) { /* noop */ } }
    auxLeg.whisperSender = null;
  }
  if (auxLeg) auxLeg.mode = SUPERVISOR_MODES.JOIN; // remains joined / listen-only
}

/**
 * Leave supervision: close ONLY the auxiliary leg. The primary Customer↔Operator PeerConnection is never
 * referenced here, so leaving can never drop the customer/operator call.
 */
export function leaveSupervision(auxLeg) {
  if (auxLeg && auxLeg.pc && typeof auxLeg.pc.close === 'function') {
    try { auxLeg.pc.close(); } catch (_) { /* noop */ }
  }
}

/**
 * Structural customer-exclusion check (used by tests and as a runtime guard). Proves the manager mic
 * sender is on the operator auxiliary PC and NOT on the Customer PC.
 * @returns {{micOnOperatorLeg:boolean, micOnCustomer:boolean, excluded:boolean}}
 */
export function verifyCustomerExcluded(auxLeg, customerPc) {
  const auxSenders = (auxLeg && auxLeg.pc && auxLeg.pc.getSenders && auxLeg.pc.getSenders()) || [];
  const custSenders = (customerPc && customerPc.getSenders && customerPc.getSenders()) || [];
  const micTrack = auxLeg && auxLeg.whisperSender && auxLeg.whisperSender.track;
  const micOnOperatorLeg = !!micTrack && auxSenders.some((s) => s.track === micTrack);
  const micOnCustomer = !!micTrack && custSenders.some((s) => s.track === micTrack);
  return { micOnOperatorLeg, micOnCustomer, excluded: micOnOperatorLeg && !micOnCustomer };
}

// ── Signaling lifecycle (pure; the WS transport + confirm HTTP are injected, so this is unit-testable) ──
// The supervisor and operator each drive ONE auxiliary PC over the existing authenticated /webrtc relay
// using supervisor-* frames bound to the authoritative call_id + supervision_session_id. The Customer is
// never an offer/answer/ICE target here.

/** Bind ICE + connection-state to the injected transport. onConnected fires once, on 'connected'. */
export function wireSupervisorLegTransport(auxLeg, { sendSignal, onConnected } = {}) {
  if (!auxLeg || !auxLeg.pc) return;
  auxLeg.pc.onicecandidate = (e) => {
    if (e && e.candidate && typeof sendSignal === 'function') {
      sendSignal({ type: 'supervisor-ice', target_role: 'operator', target: auxLeg.targetUserId,
        conn_id: auxLeg.connId, candidate: e.candidate });
    }
  };
  auxLeg.pc.onconnectionstatechange = () => {
    if (auxLeg.pc.connectionState === 'connected' && !auxLeg._confirmed) {
      auxLeg._confirmed = true;
      if (typeof onConnected === 'function') onConnected();
    }
  };
}

/** Supervisor: create the offer for the auxiliary leg (never addressed to the customer). */
export async function createSupervisorOffer(auxLeg) {
  const offer = await auxLeg.pc.createOffer();
  await auxLeg.pc.setLocalDescription(offer);
  return offer;
}
export async function applySupervisorAnswer(auxLeg, answer) { await auxLeg.pc.setRemoteDescription(answer); }
export async function addSupervisorIce(auxLeg, candidate) {
  try { await auxLeg.pc.addIceCandidate(candidate); } catch (_) { /* pre-remote candidate — safe to drop */ }
}

/**
 * OPERATOR side — build the auxiliary leg from an authenticated supervisor-offer, adding the LISTEN FEED
 * (customer-remote + operator-local audio) as ADDITIONAL tracks on the auxiliary PC. It never removes a
 * track from the primary call, never targets the customer, and never renegotiates the primary PC.
 * @param {{call_id?:string, supervision_session_id?:string, target_role?:string}} offerMsg
 * @param {{expectedCallId:string, createPeerConnection:Function, iceServers?:Array, listenTracks:Array}} deps
 */
export function buildOperatorSupervisionLeg(offerMsg, { expectedCallId, createPeerConnection, iceServers, listenTracks } = {}) {
  if (!offerMsg || !offerMsg.supervision_session_id) throw new Error('supervision: missing supervision_session_id');
  if (expectedCallId && offerMsg.call_id && offerMsg.call_id !== expectedCallId) {
    throw new Error('supervision: signal call_id does not match the active call');
  }
  const pc = createPeerConnection({ iceServers: iceServers || [] });
  const leg = { pc, supervisionSessionId: offerMsg.supervision_session_id, role: 'operator_aux',
                listenSenders: [], whisperReceiver: null };
  // Attach the listen feed as ADDITIONAL tracks (customer remote + operator local) — never detach from primary.
  (listenTracks || []).forEach((lt) => { if (lt && lt.track) leg.listenSenders.push(pc.addTrack(lt.track, lt.stream)); });
  return leg;
}

/** Operator: answer the supervisor offer (setRemote + createAnswer + setLocal). */
export async function answerOperatorSupervisionLeg(leg, offer) {
  await leg.pc.setRemoteDescription(offer);
  const answer = await leg.pc.createAnswer();
  await leg.pc.setLocalDescription(answer);
  return answer;
}

/** Structural check for the OPERATOR plane: the auxiliary leg is a distinct PC and shares no sender with
 *  the primary Customer↔Operator PC; the customer PC is never a supervision target. */
export function verifyOperatorLegIsolation(auxLeg, primaryPc) {
  if (!auxLeg || !auxLeg.pc || !primaryPc) return { distinctPc: false, sharesNoSender: false };
  const distinctPc = auxLeg.pc !== primaryPc;
  const auxSenders = (auxLeg.pc.getSenders && auxLeg.pc.getSenders()) || [];
  const primarySenders = (primaryPc.getSenders && primaryPc.getSenders()) || [];
  const sharesNoSender = auxSenders.every((s) => !primarySenders.includes(s));
  return { distinctPc, sharesNoSender, isolated: distinctPc && sharesNoSender };
}
